import * as XLSX from "xlsx";

export const monthLabel = (m) => {
  const [y, mo] = m.split("-").map(Number);
  return new Date(y, mo - 1, 1).toLocaleString("en-GB", { month: "long", year: "numeric" });
};

export const shiftMonth = (m, delta) => {
  const [y, mo] = m.split("-").map(Number);
  const d = new Date(y, mo - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export const fmtDate = (d) => (d ? d.split("-").reverse().join("/") : "");

export const statusOf = (r) => {
  if (r.balance < 0) return "receivable";
  if (r.settled_amount && r.balance === 0) return "settled";
  if (r.paid) return "paid";
  if (r.paid_amount > 0) return "part";
  return "pending";
};

export const balanceLabel = (b) => (b < 0 ? "Receivable" : b > 0 ? "Payable" : "Settled");

const KEYS = ["salary", "earned", "brought_forward", "bonus", "advance", "deduct", "payable", "paid_amount", "balance"];

export const totalsOf = (rows) =>
  rows.reduce((t, r) => {
    KEYS.forEach((k) => { t[k] += r[k] || 0; });
    return t;
  }, Object.fromEntries(KEYS.map((k) => [k, 0])));

export const curOf = (r) => r.currency || "INR";

export const groupTotals = (rows) =>
  ["INR", "AED"]
    .map((code) => {
      const rs = rows.filter((r) => curOf(r) === code);
      return { code, count: rs.length, t: totalsOf(rs) };
    })
    .filter((g) => g.count > 0);

export const combineGroups = (groups, fx) => {
  if (!fx?.aed_to_inr || groups.length < 2) return null;
  const to = fx.combine_in;
  const factor = (code) => (code === to ? 1 : code === "AED" ? fx.aed_to_inr : 1 / fx.aed_to_inr);
  const t = Object.fromEntries(KEYS.map((k) => [k, 0]));
  groups.forEach((g) => KEYS.forEach((k) => { t[k] += g.t[k] * factor(g.code); }));
  KEYS.forEach((k) => { t[k] = Math.round(t[k] * 100) / 100; });
  return { code: to, count: groups.reduce((n, g) => n + g.count, 0), t, combined: true };
};

export const exportExcel = (rows, month, combined, fx) => {
  const line = (r, type = r.bonus_type || "") => [r.salary, r.earned, r.brought_forward, r.bonus || 0, type, r.advance, r.deduct, r.payable, r.paid_amount, r.balance];
  const data = [
    [`Salary sheet – ${monthLabel(month)} – 30-day month`], [],
    ["Date of joining", "Staff name", "Agent name", "Currency", "Days", "Salary amt", "Earned", "B/F balance", "Bonus", "Bonus type", "Advance", "Deduct", "Payable salary", "Paid", "Balance c/f", "Balance type", "Note"],
    ...rows.map((r) => [fmtDate(r.joining_date), r.staff_name, r.agent_name, curOf(r), r.days, ...line(r), balanceLabel(r.balance), r.note || ""]),
    ...groupTotals(rows).map((g) => ["", `TOTAL ${g.code}`, "", g.code, "", ...line(g.t, ""), ""]),
    ...(combined ? [["", `COMBINED IN ${combined.code}`, `1 AED = ₹${fx.aed_to_inr}`, combined.code, "", ...line(combined.t, ""), ""]] : []),
  ];
  const ws = XLSX.utils.aoa_to_sheet(data);
  ws["!cols"] = [14, 20, 18, 9, 6, 12, 12, 12, 12, 12, 12, 12, 15, 12, 12, 12, 24].map((w) => ({ wch: w }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, month);
  XLSX.writeFile(wb, `salary-${month}.xlsx`);
};

export const exportYearExcel = ({ staff, year, months, totals, current_balance }) => {
  const currency = curOf(staff);
  const data = [
    [`Salary statement ${year} – ${staff.name} (${currency})`],
    [`Agent: ${staff.agent_name || "-"}`, `Joined: ${fmtDate(staff.joining_date) || "-"}`, `Monthly salary: ${staff.base_salary}`], [],
    ["Month", "Days", "Earned", "B/F", "Bonus", "Bonus type", "Advance", "Deduct", "Payable", "Paid", "Balance c/f", "Note"],
    ...months.map((r) => [monthLabel(r.month), r.days, r.earned, r.brought_forward, r.bonus || 0, r.bonus_type || "", r.advance, r.deduct, r.payable, r.paid_amount, r.balance, r.note || ""]),
    ["TOTAL", "", totals.earned, "", totals.bonus || 0, "", totals.advance, totals.deduct, "", totals.paid_amount, "", ""],
    [], ["Current balance", current_balance, balanceLabel(current_balance)],
  ];
  const ws = XLSX.utils.aoa_to_sheet(data);
  ws["!cols"] = [16, 6, 12, 12, 12, 12, 12, 12, 12, 12, 12, 24].map((w) => ({ wch: w }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, String(year));
  XLSX.writeFile(wb, `statement-${staff.name.replace(/\s+/g, "_")}-${year}.xlsx`);
};
